import { useCallback, useState } from 'react';
import { pexelsApiService, PexelsVideo } from './services/api';

export function usePexelsVideosCore() {
  const [videos, setVideos] = useState<PexelsVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentQuery, setCurrentQuery] = useState('');

  const searchVideos = useCallback(async (query: string, reset: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      if (reset) {
        setVideos([]);
        setCurrentPage(1);
        setCurrentQuery(query);
      }

      const response = await pexelsApiService.searchVideos(
        query, 
        reset ? 1 : currentPage + 1, 
        20
      );

      if (reset) {
        setVideos(response.videos);
        setCurrentPage(1);
      } else {
        setVideos(prev => [...prev, ...response.videos]);
        setCurrentPage(prev => prev + 1);
      }

      setHasMore(!!response.next_page);
    } catch (err: any) {
      setError(err.message || 'Error al buscar videos');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const loadMoreVideos = useCallback(() => {
    if (!loading && hasMore && currentQuery) {
      searchVideos(currentQuery, false);
    }
  }, [loading, hasMore, currentQuery, searchVideos]);

  const loadPopularVideos = useCallback(async (reset: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      if (reset) {
        setVideos([]);
        setCurrentPage(1);
        setCurrentQuery('');
      }

      const response = await pexelsApiService.getPopularVideos(
        reset ? 1 : currentPage + 1, 
        20
      );

      if (reset) {
        setVideos(response.videos);
        setCurrentPage(1);
      } else {
        setVideos(prev => [...prev, ...response.videos]);
        setCurrentPage(prev => prev + 1);
      }

      setHasMore(!!response.next_page);
    } catch (err: any) {
      setError(err.message || 'Error al cargar videos');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const downloadVideo = useCallback(async (video: PexelsVideo): Promise<Blob> => {
    try {
      return await pexelsApiService.downloadVideo(video);
    } catch (err: any) {
      throw new Error('Error al descargar el video');
    }
  }, []);

  const clearVideos = useCallback(() => {
    setVideos([]);
    setCurrentPage(1);
    setCurrentQuery('');
    setHasMore(true);
    setError(null);
  }, []);

  return {
    videos,
    loading,
    error,
    hasMore,
    searchVideos,
    loadMoreVideos,
    loadPopularVideos,
    downloadVideo,
    clearVideos
  };
}
